/**
 * Canonical ORDAN language registry (web). Mobile mirrors via shared/language-registry.json.
 * A language is selectable in production only when productionSupported is true.
 */

export type TextDirection = 'ltr' | 'rtl';

export type OrdanLanguageCode = 'en' | 'ar' | 'hi' | 'es' | 'fr' | 'de';

export interface OrdanLanguageDefinition {
  code: OrdanLanguageCode;
  nativeName: string;
  englishName: string;
  dir: TextDirection;
  /** BCP-47 tag for Intl / formatters */
  bcp47: string;
  /** dayjs locale pack name */
  dayjsLocale: string;
  /** Key used to load antd locale module */
  antdLocaleKey: 'en_US' | 'ar_EG' | 'hi_IN' | 'es_ES' | 'fr_FR' | 'de_DE';
  productionSupported: boolean;
}

export const LANGUAGE_REGISTRY: readonly OrdanLanguageDefinition[] = [
  {
    code: 'en',
    nativeName: 'English',
    englishName: 'English',
    dir: 'ltr',
    bcp47: 'en-AE',
    dayjsLocale: 'en',
    antdLocaleKey: 'en_US',
    productionSupported: true,
  },
  {
    code: 'ar',
    nativeName: 'العربية',
    englishName: 'Arabic',
    dir: 'rtl',
    bcp47: 'ar-AE',
    dayjsLocale: 'ar',
    antdLocaleKey: 'ar_EG',
    productionSupported: true,
  },
  {
    code: 'hi',
    nativeName: 'हिन्दी',
    englishName: 'Hindi',
    dir: 'ltr',
    bcp47: 'hi-IN',
    dayjsLocale: 'hi',
    antdLocaleKey: 'hi_IN',
    productionSupported: true,
  },
  {
    code: 'es',
    nativeName: 'Español',
    englishName: 'Spanish',
    dir: 'ltr',
    bcp47: 'es-ES',
    dayjsLocale: 'es',
    antdLocaleKey: 'es_ES',
    productionSupported: false,
  },
  {
    code: 'fr',
    nativeName: 'Français',
    englishName: 'French',
    dir: 'ltr',
    bcp47: 'fr-FR',
    dayjsLocale: 'fr',
    antdLocaleKey: 'fr_FR',
    productionSupported: false,
  },
  {
    code: 'de',
    nativeName: 'Deutsch',
    englishName: 'German',
    dir: 'ltr',
    bcp47: 'de-DE',
    dayjsLocale: 'de',
    antdLocaleKey: 'de_DE',
    productionSupported: false,
  },
] as const;

export const DEFAULT_UI_LANGUAGE: OrdanLanguageCode = 'en';
export const DEFAULT_DOCUMENT_LANGUAGE: OrdanLanguageCode = 'en';

const byCode = new Map(LANGUAGE_REGISTRY.map((l) => [l.code, l]));

export function getLanguage(code: string | null | undefined): OrdanLanguageDefinition {
  if (code && byCode.has(code as OrdanLanguageCode)) {
    return byCode.get(code as OrdanLanguageCode)!;
  }
  return byCode.get(DEFAULT_UI_LANGUAGE)!;
}

export function isRtlLanguage(code: string | null | undefined): boolean {
  return getLanguage(code).dir === 'rtl';
}

export function productionLanguages(): OrdanLanguageDefinition[] {
  return LANGUAGE_REGISTRY.filter((l) => l.productionSupported);
}

export function selectableLanguageCodes(): OrdanLanguageCode[] {
  return productionLanguages().map((l) => l.code);
}

/**
 * Preference resolution: explicit user → company/system default → app default (en).
 * Never overwrite an explicit user choice with browser locale.
 */
export function resolveUiLanguage(opts: {
  userLanguage?: string | null;
  companyDefaultLanguage?: string | null;
  browserLanguage?: string | null;
  allowBrowserSeed?: boolean;
}): OrdanLanguageCode {
  const user = normalizeLanguageCode(opts.userLanguage);
  if (user) return ensureProductionOrFallback(user);

  const company = normalizeLanguageCode(opts.companyDefaultLanguage);
  if (company) return ensureProductionOrFallback(company);

  if (opts.allowBrowserSeed) {
    const browser = normalizeLanguageCode(opts.browserLanguage);
    if (browser) return ensureProductionOrFallback(browser);
  }

  return DEFAULT_UI_LANGUAGE;
}

export function normalizeLanguageCode(raw: string | null | undefined): OrdanLanguageCode | null {
  if (!raw || typeof raw !== 'string') return null;
  const base = raw.trim().toLowerCase().split(/[-_]/)[0];
  if (byCode.has(base as OrdanLanguageCode)) return base as OrdanLanguageCode;
  return null;
}

function ensureProductionOrFallback(code: OrdanLanguageCode): OrdanLanguageCode {
  const def = getLanguage(code);
  return def.productionSupported ? def.code : DEFAULT_UI_LANGUAGE;
}

/** Identifiers that must keep LTR isolation in RTL layouts. */
export const BIDI_ISOLATE_CLASS = 'ordan-bidi-isolate';
